/**
 * The single configured API client.
 *
 * Every request in the application goes through here. Scattering `fetch` calls through
 * components would mean scattering credential handling, error normalisation and base-URL
 * logic with them.
 *
 * `credentials: 'include'` because the browser authenticates with an httpOnly session
 * cookie. There is no token in JavaScript, nothing in localStorage, and nothing for an XSS
 * payload to read.
 */
import createClient, { type Middleware } from 'openapi-fetch'

import { env } from '@/config/env'
import { ApiError, type ErrorType } from './errors'

import type { paths } from '@/types/generated/api'

/**
 * Listeners notified when the server says this browser is no longer signed in.
 *
 * A session can end while the tab is open — it expires, the user signs out on another
 * device, an administrator revokes it. Without this, the app keeps rendering a stale shell
 * and every panel fails separately with its own error card, which reads as "the backend is
 * broken" rather than "you have been signed out".
 */
const unauthorizedListeners = new Set<() => void>()

export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

/** Turns a non-2xx response into a typed ApiError, so callers never see a raw Response. */
const errorMiddleware: Middleware = {
  async onResponse({ request, response }) {
    if (response.ok) return response

    // A 401 from the sign-in endpoints means "those credentials are wrong", not "your
    // session ended" — announcing a sign-out there would be a lie, and would clear state
    // the user is in the middle of using.
    if (response.status === 401 && !new URL(request.url).pathname.startsWith('/api/auth/')) {
      for (const listener of unauthorizedListeners) listener()
    }

    let errorType: ErrorType = 'HTTP_ERROR'
    let message = `Request failed (${response.status}).`
    let retryable = response.status >= 500
    // The error envelope carries the request id in the body; the middleware also sets the
    // header. Prefer the body, because a proxy can drop a header and the body is the
    // contract. Fall back to the header so a failure raised before the handler still has one.
    let requestId: string | null = response.headers.get('x-request-id')

    try {
      const body = await response.clone().json()
      if (body && typeof body === 'object') {
        if (typeof body.error_type === 'string') errorType = body.error_type as ErrorType
        if (typeof body.request_id === 'string') requestId = body.request_id
        if (typeof body.retryable === 'boolean') retryable = body.retryable
        if (typeof body.message === 'string') message = body.message
        // FastAPI's own handlers use `detail`.
        else if (typeof body.detail === 'string') message = body.detail
      }
    } catch {
      // A non-JSON body (a proxy error page, say) keeps the generic message.
    }

    throw new ApiError({ errorType, message, status: response.status, requestId, retryable })
  },
}

export const api = createClient<paths>({
  baseUrl: env.apiBaseUrl || '/',
  credentials: 'include',
  headers: { Accept: 'application/json' },
  // Resolve `fetch` at call time rather than at client creation. openapi-fetch would
  // otherwise capture whatever `globalThis.fetch` was when this module first loaded, which
  // silently defeats any later replacement — including the request mocking the test suite
  // installs after modules are imported.
  fetch: (request) => globalThis.fetch(request),
})

api.use(errorMiddleware)

/**
 * Unwrap an openapi-fetch result.
 *
 * The middleware throws on failure, so reaching here with no data means the response was a
 * 204 or something equally unexpected for a typed GET.
 */
export function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.data === undefined) {
    throw new ApiError({
      errorType: 'INTERNAL_ERROR',
      message: 'The server returned an empty response.',
      status: 0,
    })
  }
  return result.data
}

/** Network-level failures (server down, DNS, offline) never reach the middleware. */
export async function guard<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation()
  } catch (error) {
    if (error instanceof ApiError) throw error
    throw new ApiError({
      errorType: 'NETWORK_ERROR',
      message: 'Cannot reach the API. Check that the backend is running.',
      status: 0,
      retryable: true,
    })
  }
}
